import { useCallback } from 'react';
import { PlanImporterType, useGetUsageFieldsLazyQuery, UserOrganizationRole } from 'models/graphql';
import { useSelectedOrganization } from './useSelectedOrganization';
import { gql } from '@apollo/client';

gql`
  fragment UsageFields on PlanUsageType {
    __typename
    posts
    socialProfiles
    activeCategories
    autoImports
    importSources
    aiCredits
    users
  }
  query GetUsageFields {
    me {
      organizations {
        plan {
          usage {
            ...UsageFields
            __typename
          }
        }
      }
    }
  }
`;
const INFINITE_ADDON = -1;
export const useOrganizationPlan = () => {
  const { selectedOrganization } = useSelectedOrganization();
  const [getUsageFieldsLazyQuery] = useGetUsageFieldsLazyQuery();
  const accountPlan = selectedOrganization?.plan;
  const importType = accountPlan?.importerType;
  const isTrial = accountPlan?.name === 'Free Trial';
  const analyticsType = accountPlan?.analyticsType;
  const planLimits = accountPlan?.limits;
  const usagePlan = accountPlan?.usage;
  const limitActiveCategories = planLimits?.activeCategories ?? 0;
  const usageCategories = usagePlan?.activeCategories ?? 0;
  const limitPosts = planLimits?.posts ?? 0;
  const usagePosts = usagePlan?.posts ?? 0;
  const limitSocialProfiles = planLimits?.socialProfiles ?? 0;
  const usageSocialProfiles = usagePlan?.socialProfiles ?? 0;
  const limitAutoImports = planLimits?.autoImports ?? 0;
  const usageAutoImports = usagePlan?.autoImports ?? 0;
  const limitImportSource = planLimits?.importSources ?? 0;
  const usageImportSource = usagePlan?.importSources ?? 0;
  const limitAiCaption = planLimits?.aiCredits ?? 0;
  const usageAiCaption = usagePlan?.aiCredits ?? 0;
  const limitUsers = planLimits?.users ?? 0;
  const usageUsers = usagePlan?.users ?? 0;

  const canAddCategory = useCallback(() => {
    if (limitActiveCategories === INFINITE_ADDON) {
      return true;
    }
    return limitActiveCategories > usageCategories;
  }, [limitActiveCategories, usageCategories]);

  const canAddUser = useCallback(() => {
    if (limitUsers === INFINITE_ADDON) {
      return true;
    }
    return limitUsers > usageUsers;
  }, [limitUsers, usageUsers]);

  const canAddPosts = useCallback(
    (qtyPostToBeAdded: number) => {
      if (limitPosts === INFINITE_ADDON) {
        return true;
      }
      return limitPosts >= usagePosts + qtyPostToBeAdded;
    },
    [limitPosts, usagePosts]
  );

  const canUseAiCaptions = (hasLimitAlreadyBeenReachead: boolean) => {
    if (limitAiCaption === INFINITE_ADDON) {
      return true;
    }
    if (hasLimitAlreadyBeenReachead) {
      return false;
    }
    return limitAiCaption > usageAiCaption;
  };

  const canAddSocialProfile = useCallback(
    (newQtyProfiles: number) => {
      if (limitSocialProfiles === INFINITE_ADDON) {
        return true;
      }
      return limitSocialProfiles >= usageSocialProfiles + newQtyProfiles;
    },
    [limitSocialProfiles, usageSocialProfiles]
  );

  const canAddNewAutoImport = useCallback(() => {
    if (limitAutoImports === INFINITE_ADDON) {
      return true;
    }
    return limitAutoImports > usageAutoImports;
  }, [limitAutoImports, usageAutoImports]);

  const canImportFromNetwork = useCallback(() => {
    return importType !== PlanImporterType.BASIC_IMPORTER;
  }, [importType]);

  const canSaveSource = useCallback(() => {
    if (limitImportSource === INFINITE_ADDON) {
      return true;
    }

    return limitImportSource > usageImportSource;
  }, [limitImportSource, usageImportSource]);
  const remainingProfiles =
    limitSocialProfiles === INFINITE_ADDON ? INFINITE_ADDON : limitSocialProfiles - usageSocialProfiles;
  const remainingPosts = limitPosts === INFINITE_ADDON ? INFINITE_ADDON : limitPosts - usagePosts;
  return {
    limitActiveCategories,
    limitPosts,
    limitSocialProfiles,
    usageSocialProfiles,
    limitAiCaption,
    canAddCategory,
    canAddPosts,
    canAddSocialProfile,
    canAddNewAutoImport,
    canImportFromNetwork,
    canSaveSource,
    canUseAiCaptions,
    canAddUser,
    isOwner: selectedOrganization?.myUserAssignment?.role === UserOrganizationRole.OWNER,
    importType,
    analyticsType,
    isTrial,
    remainingProfiles,
    remainingPosts,
    getUsageFieldsLazyQuery
  };
};
